/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import clsx from 'clsx';
import { makeStyles } from '@material-ui/core/styles';
import {
  Snackbar,
  Avatar,
  CssBaseline,
  Link,
  Paper,
  Box,
  Grid,
  Typography,
  CircularProgress,
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { LockOutlined as LockOutlinedIcon } from '@material-ui/icons';
import {
  clearMessage,
  selectIsAuthenticated,
  selectIsPending,
  selectMessage,
  selectSeverity,
  selectIsNewPasswordRequired,
  selectIsForgotPassword,
  selectIsUserRegistration,
  selectIsUnverifiedEmail,
} from '../store/authSlice';
import NewPasswordForm from '../components/forms/NewPasswordForm';
import ForgotPasswordForm from '../components/forms/ForgotPasswordForm';
import SignInForm from '../components/forms/SignInForm';
import SignUpForm from '../components/forms/SignUpForm';
import EmailVerificationForm from '../components/forms/EmailVerificationForm';

function Copyright() {
  return (
    <Typography variant="body2" color="textSecondary" align="center">
      {'Copyright © '}
      <Link color="inherit" href="https://guestranger.com/">
        Guest Ranger LLC
      </Link>
      {' '}
      {new Date().getFullYear()}
      {'.'}
    </Typography>
  );
}

const useStyles = makeStyles((theme) => ({
  root: {
    height: '100vh',
  },
  image: {
    backgroundImage: 'url(https://source.unsplash.com/random?travel&city)',
    backgroundRepeat: 'no-repeat',
    backgroundColor:
      theme.palette.type === 'light' ? theme.palette.grey[50] : theme.palette.grey[900],
    backgroundSize: 'cover',
    backgroundPosition: 'center',
  },
  paper: {
    margin: theme.spacing(8, 4),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  avatar: {
    margin: theme.spacing(1),
    backgroundColor: theme.palette.secondary.main,
  },
  form: {
    width: '100%',
    marginTop: theme.spacing(1),
  },
  submit: {
    margin: theme.spacing(3, 0, 2),
  },
  elementHidden: {
    display: 'none',
  },
  formContainer: {
    width: '100%',
  },
  pendingProgress: {
    color: theme.palette.primary.main,
    position: 'absolute',
    marginTop: '5px',
    zIndex: 1,
  },
}));

export default function Login() {
  const classes = useStyles();
  const history = useHistory();
  const dispatch = useDispatch();
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const isNewPasswordRequired = useSelector(selectIsNewPasswordRequired);
  const isForgotPassword = useSelector(selectIsForgotPassword);
  const isUserRegistration = useSelector(selectIsUserRegistration);
  const isUnverifiedEmail = useSelector(selectIsUnverifiedEmail);
  const isPending = useSelector(selectIsPending);
  const message = useSelector(selectMessage);
  const severity = useSelector(selectSeverity);

  const handleClose = () => {
    dispatch(clearMessage());
  };

  useEffect(() => {
    if (isAuthenticated) {
      history.push('/');
    }
  }, [isAuthenticated, history]);

  return (
    <Grid container component="main" className={classes.root}>
      <CssBaseline />
      <Snackbar open={!!message} autoHideDuration={6000} onClose={handleClose}>
        <Alert onClose={handleClose} severity={severity}>{message}</Alert>
      </Snackbar>
      <Grid item xs={false} sm={4} md={7} className={classes.image} />
      <Grid item xs={12} sm={8} md={5} component={Paper} elevation={6} square>
        <div className={classes.paper}>
          <Avatar className={classes.avatar}>
            <LockOutlinedIcon />
          </Avatar>
          {isPending && <CircularProgress size={46} className={classes.pendingProgress} />}
          <div className={clsx(classes.formContainer,
            (isUserRegistration || isForgotPassword || isNewPasswordRequired || isUnverifiedEmail)
              && classes.elementHidden)}
          >
            <Typography align="center" component="h1" variant="h5">Sign in</Typography>
            <SignInForm />
          </div>
          <div className={clsx(classes.formContainer,
            (isUserRegistration || isForgotPassword || !isNewPasswordRequired || isUnverifiedEmail)
              && classes.elementHidden)}
          >
            <Typography align="center" component="h1" variant="h5">New password required</Typography>
            <NewPasswordForm />
          </div>
          <div className={clsx(classes.formContainer,
            (isUserRegistration || !isForgotPassword || isNewPasswordRequired || isUnverifiedEmail)
              && classes.elementHidden)}
          >
            <Typography align="center" component="h1" variant="h5">Forgot password?</Typography>
            <ForgotPasswordForm />
          </div>
          <div className={clsx(classes.formContainer,
            (!isUserRegistration || isForgotPassword || isNewPasswordRequired || isUnverifiedEmail)
              && classes.elementHidden)}
          >
            <Typography align="center" component="h1" variant="h5">Sign up</Typography>
            <SignUpForm />
          </div>
          <div className={clsx(classes.formContainer,
            (isUserRegistration || isForgotPassword || isNewPasswordRequired || !isUnverifiedEmail)
              && classes.elementHidden)}
          >
            <Typography align="center" component="h1" variant="h5">Verify Email</Typography>
            <EmailVerificationForm />
          </div>
          <Box mt={5}>
            <Copyright />
          </Box>
        </div>
      </Grid>
    </Grid>
  );
}
